import axios, { HttpStatusCode } from 'axios';
import {
  AuthInvalidPasswordError,
  AuthServerError,
  AuthUserNotFoundError,
  OIDCError,
  TokenExpiredError,
  TokenInvalidError,
  TokenNotFoundError,
} from '../../corelogic/domain/errors/authentication.error';
import { AUTH_API_ERROR_MESSAGES } from '../../corelogic/domain/errors/gateway.errorMessages/authentication.error.messages';
import { BackendAuthenticatorGateway } from '../../corelogic/domain/ports/authenticatorBackend.port';
import { OicdAuthenticatorGateway } from '../../corelogic/domain/ports/authenticatorOidc.port';
import { getSecureRequestHeaders } from '../../corelogic/domain/utils/securityHeaders.utils';

export class APIAuthenticatorGateway implements BackendAuthenticatorGateway {
  private apiEndpoint: string;
  private oidcAuthenticator: OicdAuthenticatorGateway;

  private handleAuthError(error: unknown, expectToken: boolean = false): never {
    if (axios.isAxiosError(error)) {
      if (expectToken && !error.response?.headers?.authorization) {
        throw new TokenNotFoundError(AUTH_API_ERROR_MESSAGES.TOKEN_NOT_FOUND);
      }

      switch (error.response?.status) {
        case HttpStatusCode.BadRequest:
          if (error.response?.data?.error.includes('password')) {
            throw new AuthInvalidPasswordError(
              AUTH_API_ERROR_MESSAGES.AUTH_INVALID_PASSWORD,
            );
          }
          throw new TokenInvalidError(AUTH_API_ERROR_MESSAGES.INVALID_TOKEN);
        case HttpStatusCode.Unauthorized:
          throw new TokenExpiredError(AUTH_API_ERROR_MESSAGES.TOKEN_EXPIRED);
        case HttpStatusCode.NotFound:
          throw new AuthUserNotFoundError(
            AUTH_API_ERROR_MESSAGES.AUTH_USER_NOT_FOUND,
          );
        default:
          throw new AuthServerError(AUTH_API_ERROR_MESSAGES.SERVER_ERROR);
      }
    }
    throw new AuthServerError(AUTH_API_ERROR_MESSAGES.SERVER_ERROR);
  }

  constructor(
    apiEndpoint: string,
    oidcAuthenticator: OicdAuthenticatorGateway,
  ) {
    this.apiEndpoint = apiEndpoint;
    this.oidcAuthenticator = oidcAuthenticator;
  }

  async verifyToken(token: string): Promise<void> {
    try {
      await axios.post(
        `${this.apiEndpoint}/verify`,
        {},
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );
    } catch (error) {
      throw this.handleAuthError(error, false);
    }
  }

  async refreshToken(token: string): Promise<string> {
    try {
      const response = await axios.post(
        `${this.apiEndpoint}/refresh-token`,
        {},
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );

      if (!response.data || !response.data.token) {
        throw new TokenNotFoundError(AUTH_API_ERROR_MESSAGES.TOKEN_NOT_FOUND);
      }

      // Use OIDC authenticator to exchange ID token with custom token
      return this.oidcAuthenticator.exchangeCustomToken(response.data.token);
    } catch (error) {
      if (error instanceof OIDCError) {
        throw error;
      }
      throw this.handleAuthError(error, true);
    }
  }

  async changePassword(token: string, newPassword: string): Promise<string> {
    try {
      const response = await axios.post(
        `${this.apiEndpoint}/change-password`,
        {
          newPassword,
        },
        {
          headers: {
            ...getSecureRequestHeaders(token),
          },
        },
      );

      if (!response.data || !response.data.token) {
        throw new TokenNotFoundError(AUTH_API_ERROR_MESSAGES.TOKEN_NOT_FOUND);
      }
      // Exchange custom token from backend for ID token
      return this.oidcAuthenticator.exchangeCustomToken(response.data.token);
    } catch (error) {
      if (error instanceof OIDCError) {
        throw error;
      }
      throw this.handleAuthError(error, true);
    }
  }
}
